import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { getStripeEnvironment } from "@/lib/stripe";

/**
 * True if the user has ever had a subscription row in this environment
 * (active, canceled, expired — anything). Used to decide whether a fresh
 * checkout should include a 30-day Stripe trial (first-time subscribers only).
 *
 * Backed by the `has_ever_subscribed` SQL helper for a cheap existence check
 * that respects RLS via SECURITY DEFINER.
 */
export function useHasEverSubscribed(): { loading: boolean; hasEverSubscribed: boolean } {
  const { user } = useAuth();
  const [hasEverSubscribed, setHasEverSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setHasEverSubscribed(false);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.rpc("has_ever_subscribed", {
        user_uuid: user.id,
        check_env: getStripeEnvironment(),
      });
      if (!cancelled) {
        if (error) {
          console.error("has_ever_subscribed failed:", error);
          setHasEverSubscribed(false);
        } else {
          setHasEverSubscribed(!!data);
        }
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  return { loading, hasEverSubscribed };
}
